import React, { useState, useEffect } from 'react';

const EditModal = ({ pattern, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [editedJson, setEditedJson] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Defined inside the effect — only used here, and it keeps the
    // dependency array honest (react-hooks/exhaustive-deps).
    const fetchPatternDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/prerequisites/pattern/${encodeURIComponent(pattern)}`
        );
        const data = await response.json();
        setDetails(data);
        setEditedJson(JSON.stringify(data.parsed, null, 2));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching pattern details:', error);
        setError('Failed to load pattern details');
        setLoading(false);
      }
    };
    fetchPatternDetails();
  }, [pattern]);

  const handleSave = async () => {
    try {
      // Validate JSON
      const parsed = JSON.parse(editedJson);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/prerequisites/pattern/${encodeURIComponent(pattern)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            parsed: parsed,
            reviewer: 'admin'
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        onSave(result);
        onClose();
      } else {
        setError('Failed to save changes');
      }
    } catch (e) {
      setError('Invalid JSON format');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">Edit Prerequisite Pattern</h2>
          <p className="text-sm text-gray-600 mt-1">
            Pattern affects {details?.total_courses || 0} courses
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {/* Original Pattern */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Original Pattern
              </label>
              <div className="p-3 bg-gray-100 rounded-md font-mono text-sm">
                {pattern}
              </div>
            </div>

            {/* Affected Courses */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Affected Courses
              </label>
              <div className="flex flex-wrap gap-2">
                {details?.courses.slice(0, 10).map(course => (
                  <span key={course.id} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                    {course.code}
                  </span>
                ))}
                {details?.courses.length > 10 && (
                  <span className="px-2 py-1 text-gray-500 text-sm">
                    +{details.courses.length - 10} more
                  </span>
                )}
              </div>
            </div>

            {/* JSON Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parsed Structure (JSON)
              </label>
              <textarea
                value={editedJson}
                onChange={(e) => setEditedJson(e.target.value)}
                className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm"
                spellCheck={false}
              />
              {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
              )}
            </div>

            {/* Confidence Score */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parser Confidence
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      details?.confidence >= 0.9 ? 'bg-green-500' :
                      details?.confidence >= 0.7 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${(details?.confidence || 0) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-medium">
                  {((details?.confidence || 0) * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditModal;
